import { useEffect, useRef } from "react";

interface UseScrollHintOptions {
  delay?: number;
  distance?: number;
  duration?: number;
  storageKey?: string;
}

export function useScrollHint<T extends HTMLElement>({
  delay = 500,
  distance = 40,
  duration = 600,
  storageKey = "scrollHintShown",
}: UseScrollHintOptions = {}) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const alreadyShown = localStorage.getItem(storageKey);
    if (alreadyShown) return;
    if (el.scrollWidth > el.clientWidth) {
      const timeout = setTimeout(() => {
        const scrollAmount = Math.min(
          distance,
          el.scrollWidth - el.clientWidth
        );
        el.scrollTo({ left: scrollAmount, behavior: "smooth" });

        const backTimeout = setTimeout(() => {
          el.scrollTo({ left: 0, behavior: "smooth" });
          localStorage.setItem(storageKey, "true");
        }, duration);

        return () => clearTimeout(backTimeout);
      }, delay);

      return () => clearTimeout(timeout);
    }
  }, [delay, distance, duration, storageKey]);

  return ref;
}
